import { useLiveQuery } from 'dexie-react-hooks'
import React, { useEffect, useState } from 'react'
import { useLazyQuery } from 'react-apollo'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Button, Footer, Header, Input, Modal } from '../../components'
import { CONSTRUCTION_SITE_BY_CODE, TRUCK_BY_LICENSEPLATE } from '../../gql'
import { db } from '../../lib/db'
import './NewOrderStyle.scss'

const NewOrder = () => {
  const { t } = useTranslation('newOrder')
  const history = useHistory()
  const lastOrder = useLiveQuery(async () => db.offlineOrders.orderBy('id').last())

  const [customer, setCustomer] = useState('')
  const [customerId, setCustomerId] = useState(null)
  const [externalReference, setExternalReference] = useState('')
  const [forwarder, setForwarder] = useState('')
  const [constructionNb, setConstructionNb] = useState('')
  const [constructionId, setConstructionId] = useState(null)
  const [validating, setValidating] = useState(false)
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [plate, setPlate] = useState('')
  const [plateId, setPlateId] = useState(null)
  const [addressNumber, setAddressNumber] = useState('')
  const [error, setError] = useState(false)

  // Saisie automatique active par défaut au chargement
  const [strictValidationAddress, setStrictValidationAddress] = useState(true)
  const [strictValidationCity, setStrictValidationCity] = useState(true)

  // Modal commune pour confirmation lors du changement du mode de saisie
  const [showModal, setShowModal] = useState(false)
  const [modalTarget, setModalTarget] = useState(null) // "address" ou "city"

  const prepareAdress = (add = '') => {
    const addressParts = add.split(' ').filter((part) => part.trim())
    const number = addressParts[0] || ''
    const addr = addressParts.slice(1).join(' ')
    setAddressNumber(number)
    setAddress(addr)
  }

  useEffect(() => {
    if (lastOrder) {
      setCustomer(lastOrder.customer)
      setCustomerId(lastOrder.customerId)
      setExternalReference(lastOrder.externalReference)
      setForwarder(lastOrder.forwarder)
      setConstructionNb(lastOrder.constructionNb)
      setConstructionId(lastOrder.constructionId)
      prepareAdress(lastOrder.address)
      setCity(lastOrder.city)
      setPlate(lastOrder.plate)
      setPlateId(lastOrder.plate)
    }
  }, [lastOrder])

  const [fetchIdTruck] = useLazyQuery(TRUCK_BY_LICENSEPLATE, {
    onCompleted: (response) => {
      if (response.trucks) {
        setPlateId(response?.trucks?.nodes[0]?.id)
      }
    },
  })

  const [fetchIdConstructionSite] = useLazyQuery(CONSTRUCTION_SITE_BY_CODE, {
    onCompleted: (response) => {
      if (response.constructionSiteByCode) {
        setConstructionId(response?.constructionSiteByCode?.id)
        setCity(response?.constructionSiteByCode?.city)
        prepareAdress(response?.constructionSiteByCode?.address)
      }
    },
  })

  useEffect(() => {
    if (navigator.onLine) {
      if (constructionNb !== '') {
        fetchIdConstructionSite({
          variables: { code: constructionNb },
        })
      }

      fetchIdTruck({
        variables: { plicensePlate: plate },
      })
    }
  }, [constructionNb, plate])

  const validate = async () => {
    const requiredFields = [
      { name: 'customer', value: customer },
      { name: 'plate', value: plate },
      { name: 'addressNumber', value: addressNumber },
      { name: 'address', value: address },
      { name: 'city', value: city },
    ]

    const missing = requiredFields.filter((f) => !f.value || f.value.trim() === '')
    if (missing.length > 0) {
      setError(true)
      return
    }

    setValidating(true)

    const fullAddress =
      addressNumber.trim() && address.trim()
        ? `${addressNumber.trim()} ${address.trim()}`
        : addressNumber.trim() || address.trim()

    await db.offlineOrders.update(lastOrder.id, {
      customerId,
      customer,
      forwarder,
      plate,
      plateId,
      constructionNb,
      address: fullAddress,
      city,
      externalReference,
      constructionId,
    })

    history.push('/catalog')
  }

  const availableAddresses = useLiveQuery(async () => {
    const result = await db.addresses.toArray()
    return result.map((item) => item.address)
  }, [])

  const availableCities = useLiveQuery(async () => {
    const result = await db.cities.toArray()
    return result
      .map((item) => item.city)
      .filter((city) => city && city.trim())
      .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }))
  }, [])

  const isFormValid =
    customer?.trim() && plate?.trim() && addressNumber?.trim() && address?.trim() && city?.trim()

  const openModal = (target) => {
    setModalTarget(target)
    setShowModal(true)
  }

  const handleModalResponse = (userChoice) => {
    if (modalTarget === 'address') {
      if (strictValidationAddress) {
        // État actuel : saisie automatique
        // Si userChoice = false (Non), on active la saisie manuelle
        setStrictValidationAddress(userChoice)
      } else {
        // État actuel : saisie manuelle
        // Si userChoice = true (Oui), on active la saisie automatique
        setStrictValidationAddress(userChoice)
        // Si on passe en saisie automatique, vérifier si l'adresse actuelle existe
        if (userChoice && address) {
          // userChoice = true signifie qu'on active l'auto
          const addressExists = availableAddresses?.some(
            (addr) => addr.toUpperCase() === address.toUpperCase()
          )
          if (!addressExists) {
            setAddress('') // Vider le champ si l'adresse n'existe pas
          }
        }
      }
    } else if (modalTarget === 'city') {
      if (strictValidationCity) {
        // État actuel : saisie automatique
        // Si userChoice = false (Non), on active la saisie manuelle
        setStrictValidationCity(userChoice)
      } else {
        // État actuel : saisie manuelle
        // Si userChoice = true (Oui), on active la saisie automatique
        setStrictValidationCity(userChoice)
        // Si on passe en saisie automatique, vérifier si la ville actuelle existe
        if (userChoice && city) {
          // userChoice = true signifie qu'on active l'auto
          const villeExists = availableCities?.some(
            (ville) => ville.toUpperCase() === city.toUpperCase()
          )
          if (!villeExists) {
            setCity('') // Vider le champ si la ville n'existe pas
          }
        }
      }
    }
    setShowModal(false)
  }

  // Déterminer les textes du modal en fonction de l'état actuel
  const getModalTexts = () => {
    const fieldName = modalTarget === 'address' ? 'Adresse chantier' : 'Ville'
    const isCurrentlyAutomatic =
      modalTarget === 'address' ? strictValidationAddress : strictValidationCity

    if (isCurrentlyAutomatic) {
      // Saisie automatique active → demande si on veut la garder
      return {
        description: `Voulez-vous garder la saisie automatique pour le champ "${fieldName}" ?`,
        cancelTxt: 'Non', // Non → ne pas garder → activer saisie manuelle
        okTxt: 'Oui', // Oui → garder saisie automatique
      }
    } else {
      // Saisie manuelle active → demande si on veut activer l'automatique
      return {
        description: `Voulez-vous activer la saisie automatique pour le champ "${fieldName}" ?`,
        cancelTxt: 'Non', // Non → garder saisie manuelle
        okTxt: 'Oui', // Oui → activer saisie automatique
      }
    }
  }

  const modalTexts = getModalTexts()

  return (
    <div>
      <Header title={t('newOrder')} />
      <div className="new-order">
        <div className="parts">
          <div className="input-container">
            <p>{`${t('customer')} :*`}</p>
            <Input
              width="100%"
              placeholder={t('customerName')}
              value={customer}
              onChange={(value) => {
                setCustomer(value?.displayName)
                setCustomerId(value?.id)
              }}
              required
              error={error}
              autoComplete={true}
              type="text"
            />
          </div>

          <div className="input-container">
            <p>{`${t('externalReference')} :`}</p>
            <Input
              width="100%"
              placeholder={t('externalReference')}
              value={externalReference}
              onChange={(e) => setExternalReference(e.toUpperCase())}
              type="text"
            />
          </div>

          <div className="input-container">
            <p>{`${t('forwarder')} :`}</p>
            <Input
              width="100%"
              placeholder={t('forwarderName')}
              value={forwarder}
              onChange={(e) => setForwarder(e.toUpperCase())}
              type="text"
            />
          </div>

          <div className="input-container">
            <p>{`${t('constructionNb')} :`}</p>
            <Input
              width="100%"
              placeholder={t('constructionNb2')}
              value={constructionNb}
              onChange={(e) => setConstructionNb(e.toUpperCase())}
              type="text"
            />
          </div>
        </div>

        <div className="parts">
          {/* Adresse chantier */}
          <div className="input-container">
            <p>{`${t('constructionAddress')} :*`}</p>

            <div className="address-inputs-row">
              <Input
                required
                width="15%"
                placeholder={t('addressNumber')}
                value={addressNumber}
                onChange={(e) => setAddressNumber(e)}
                type="number"
              />
              <div className="address-field-with-button">
                <Input
                  required
                  width="100%"
                  placeholder={t('constructionAddress')}
                  value={address}
                  onChange={(value) => {
                    if (value && typeof value === 'object' && value.address) {
                      setAddress(value.address.toUpperCase())
                    } else {
                      setAddress(value.toUpperCase())
                    }
                  }}
                  type="text"
                  autoCompleteData={availableAddresses}
                  strictValidation={strictValidationAddress}
                />
                <button
                  className="unlock-btn"
                  onClick={() => openModal('address')}
                  title={
                    strictValidationAddress
                      ? 'Activer la saisie manuelle'
                      : 'Saisie manuelle active'
                  }
                >
                  <img
                    src={strictValidationAddress ? '/images/pencil.svg' : '/images/pencil-off.svg'}
                    alt="toggle-address"
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Ville */}
          <div className="input-container">
            <p>{`${t('constructionCity')} :*`}</p>

            <div className="city-field-with-button">
              <Input
                required
                width="100%"
                placeholder={t('constructionCity')}
                value={city}
                onChange={(value) => {
                  if (value && typeof value === 'object' && value.city) {
                    setCity(value.city.toUpperCase())
                  } else {
                    setCity(value.toUpperCase())
                  }
                }}
                type="text"
                autoCompleteData={availableCities}
                strictValidation={strictValidationCity}
              />
              <button
                className="unlock-btn"
                onClick={() => openModal('city')}
                title={
                  strictValidationCity ? 'Activer la saisie manuelle' : 'Saisie manuelle active'
                }
              >
                <img
                  src={strictValidationCity ? '/images/pencil.svg' : '/images/pencil-off.svg'}
                  alt="toggle-city"
                />
              </button>
            </div>
          </div>

          {/* Plaque */}
          <div className="input-container">
            <p>{`${t('plate')} :*`}</p>
            <Input
              width="100%"
              placeholder={t('plate')}
              value={plate}
              onChange={(e) => setPlate(e.toUpperCase())}
              type="text"
              required
              error={error}
            />
          </div>
        </div>
      </div>

      <Footer>
        <Button
          text={t('quit')}
          type="outline"
          appearance="secondary"
          onClick={() => history.push('/orders')}
          width="210px"
        />
        <Button
          text={t('next')}
          width="25%"
          loading={validating}
          onClick={() => validate()}
          disabled={!isFormValid}
        />
      </Footer>

      {showModal && (
        <Modal
          title="Confirmation"
          description={modalTexts.description}
          cancelTxt={modalTexts.cancelTxt}
          okTxt={modalTexts.okTxt}
          onCancel={() => handleModalResponse(false)}
          onOk={() => handleModalResponse(true)}
        />
      )}
    </div>
  )
}

export default NewOrder
